import React, { useState, useEffect } from 'react';
import objToUrlParams from 'utils/objToUrlParams';
import styled from '@emotion/styled';
import propStyles from 'prop-styles';
import * as css from 'styles';

import { FadeIn, Grid, useKey, navigate, IconButton, LargerOnly, MobileOnly } from 'components';

const PageNav = ({ children, data, status, location }) => {
  const prev = data.offset !== 0;
  const next = data.page != data.pages; // eslint-disable-line

  const prevUrl = `${location.pathname}?${objToUrlParams({ ...location.query, page: data.page - 1 })}`;
  const nextUrl = `${location.pathname}?${objToUrlParams({ ...location.query, page: data.page + 1 })}`;

  const totalPageNumbersToShow = 1; // Number of pages to show before and after the current page
  const pages = Array.from({ length: data.pages }).map((_, idx) => idx + 1); // Create an array of page numbers [1, 2, ..., data.pages]

  const startPage = Math.max(data.page - totalPageNumbersToShow, 1);
  const endPage = Math.min(data.page + totalPageNumbersToShow, data.pages);

  // Filter pages based on the range we determined
  const visiblePages = pages.filter(
    (page) => page === 1 || (page >= startPage && page <= endPage) || page === data.pages
  );

  const navigatePrev = () => prev && navigate(prevUrl);
  const navigateNext = () => next && navigate(nextUrl);

  useKey('ArrowLeft', () => navigatePrev());
  useKey('ArrowRight', () => navigateNext());

  useEffect(() => {
    location.kind === 'push' && window.scrollTo(0, 0);
  }, []);

  return (
    <Grid
      style={{
        height: '40px',
        gridTemplateColumns: '1fr auto',
        fontFamily: css.font.heading,
        fontSize: '0.8em',
      }}
    >
      <div>
        {/* Results: {data.total} */}
        {children}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            fontFamily: css.font.numbers,
            fontWeight: css.font.regular,
          }}
        >
          <IconButton
            inline
            type="arrow"
            angle="180deg"
            disabled={status !== 'Success' || !prev}
            onClick={navigatePrev}
          />

          <LargerOnly>
            {visiblePages.map((page, idx) => {
              // If the current page in the map iteration is not sequential to the next page, insert the ellipsis
              const insertEllipsis = idx < visiblePages.length - 1 && visiblePages[idx + 1] - page > 1;

              return (
                <React.Fragment key={page}>
                  <Page
                    active={page === data.page}
                    onClick={() =>
                      navigate(
                        `${location.pathname}?${objToUrlParams({
                          ...location.query,
                          page,
                        })}`
                      )
                    }
                  >
                    {page}
                  </Page>
                  {insertEllipsis && <span style={{ margin: '0 5px' }}>...</span>}
                </React.Fragment>
              );
            })}
          </LargerOnly>
          <MobileOnly>
            {status === 'Success' ? (
              <FadeIn style={{ display: 'inline-block', width: '20px', textAlign: 'center', paddingRight: '3px' }}>
                <span>{data.page}</span>{' '}
              </FadeIn>
            ) : (
              <FadeIn style={{ display: 'inline-block', width: '20px', textAlign: 'center', paddingRight: '3px' }}>
                &nbsp;
              </FadeIn>
            )}
          </MobileOnly>
          <IconButton
            inline
            type="arrow"
            angle="0deg"
            disabled={status !== 'Success' || !next}
            onClick={navigateNext}
          />
        </div>
      </div>
    </Grid>
  );
};

const Page = styled.span((props) => ({
  cursor: 'pointer',
  display: 'inline-block',
  padding: '0px 12px',
  textDecoration: props.active ? 'underline' : 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export default PageNav;
